import { Injectable, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { UtilityService } from '@eforall/common';
import { DialogService } from '@eforall/dashboard';
import { DashboardFiltersAffect, DataFieldAndFilter, DatasetId, DisplayTypeId } from '@interfaces';
import { CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { AppDataService, FuncService, StaticDataService } from '../../../services';
import { AddDashboardDialog } from './dialog/add-dashboard.dialog';
import { getDistinctFilterFields } from './get-distinct-filter-fields';

const EMPTY_FILTERS: CompositeFilterDescriptor = { logic: 'and', filters: [] };


@Injectable({ providedIn: 'root' })
export class AddDashboardDialogService {

	private readonly appData = inject(AppDataService);
	private readonly staticData = inject(StaticDataService);
	private readonly util = inject(UtilityService);
	private readonly func = inject(FuncService);
	private readonly dialogService = inject(DialogService);
	private readonly router = inject(Router);

	private _datasetId = signal<DatasetId | undefined>(undefined);
	private _fieldsAndFilters = signal<DataFieldAndFilter[] | undefined>(undefined);
	private _displayTypeId = signal<DisplayTypeId | undefined>(undefined);
	private _filters = signal<CompositeFilterDescriptor>(EMPTY_FILTERS);
	private _filtersAffect = signal<DashboardFiltersAffect | undefined>(undefined);

	public filters = this._filters.asReadonly();
	public filtersAffect = this._filtersAffect.asReadonly();
	public readyToAdd = computed(() => this.filtersAffect() !== undefined);


	/**
	 * Returns how far along the user is in the three-step wizard.  Once they hit number 4,
	 * they will have completed all the steps and the dialog action button will be enabled,
	 * allowing them to add the dashboard.
	 */
	public currentStep = computed(() => {

		//
		// The user needs to have pressed the "Get Row Count" button AND the
		// result must include at least some rows for the step to be 4.
		//
		const filteredCount = this._filtersAffect()?.filteredCount;
		if ((filteredCount ?? 0) > 0) return 4;

		if (this._displayTypeId()) return 3;
		if (this._fieldsAndFilters()) return 2;
		return 1;
	});


	/**
	 * Open a dashboard with a three-step wizard for the user to complete in order to
	 * add a dashboard.
	 */
	public async start() {

		this.reset();

		const action = await this.dialogService.showCustom(AddDashboardDialog, {}, 1000, 700);

		if (action?.id == 'add') {

			//
			// This line is just a defensive check.  The dialog action button should
			// not have been enabled so, in theory, this cannot happen.
			//
			if (this.currentStep() !== 4) return;

			//
			// The "Add" dialog action button is only enabled if the current step
			// is 4, which means all these signals are defined.  So, we can use the
			// non-null assertion operator (bang!)
			//
			const datasetId = this._datasetId()!;
			const displayTypeId = this._displayTypeId()!;
			const filters = this._filters()!;

			const appData = await this.func.dashboard.create({ datasetId, displayTypeId, filters });
			this.appData.setLatest(appData);

			if (appData.dashboard) {
				const urlKey = appData.dashboard.urlKey;
				
				//appData.dashboard.name;
				this.router.navigate(['/' + urlKey]);

			}

			this.reset();
		}
	}


	/**
	 * Clear out all state. Called when the dialog is reopened, and upon adding a dashboard.
	 */
	public async reset() {
		this._datasetId.set(undefined);
		this._fieldsAndFilters.set(undefined);
		this._displayTypeId.set(undefined);
		this._filters.set(EMPTY_FILTERS);
		this._filtersAffect.set(undefined);
	}


	/**
	 * Step 1 of 3
	 */
	public async setDatasetId(datasetId: DatasetId) {

		this._datasetId.set(datasetId);

		const fieldsAndFilters = await this.func.dataset.getFieldsAndFilters({ datasetId });
		this._fieldsAndFilters.set(fieldsAndFilters);

		const fields = fieldsAndFilters.map(f => f.field.field).sort();

		console.log(fields.length + ' fields!');

		for (const field of fields) {
			console.log(field);
		}
	}


	/**
	 * Return the filter expressions that provide the options for
	 * the <kendo-filters> component.
	 */
	public filterExpressions = computed(() => {
		return (this._fieldsAndFilters() ?? [])
			.map(f => f.filter)
			.filter(filter => !!filter);
	});


	/**
	 * Return the name of the selected dataset.
	 */
	public datasetName = computed(() => {
		const datasetId = this._datasetId();
		if (datasetId == undefined) return '';
		const dataset = this.staticData.datasets.byId(datasetId);
		const category = this.staticData.dataset_categorys.byId(dataset.datasetCategoryId);
		return `${category.name} → ${dataset.name}`;
	});


	/**
	 * Step 2 of 3
	 */
	public async setDisplayTypeId(displayTypeId: DisplayTypeId) {
		this._displayTypeId.set(displayTypeId);
	}

	/**
	 * Each time the user adjusts the filters, capture the details.
	 */
	public async setFilters(filters: CompositeFilterDescriptor) {
		//
		// It seems the kendo_filter valueChange() event emits the same
		// object reference, so we need to make a new object to trigger
		// the signal.
		//
		this._filters.set({ ...filters });
		this._filtersAffect.set(undefined);
	}




	/**
	 * Step 3 of 3
	 */
	public async determineFiltersAffect() {

		const datasetId = this._datasetId();
		const displayTypeId = this._displayTypeId(); // Not needed here but reusing the NewDashboard interface
		const filters = this._filters();

		//
		// Defensive.  The button to test the filters is not visible unless these are set.
		//
		if (datasetId == undefined || displayTypeId == undefined) return;

		const affect = await this.func.dataset.getFiltersAffect({ datasetId, displayTypeId, filters });
		this._filtersAffect.set(affect);
	}



	/**
	 * Return details of the distinct set of fields used in the filters,
	 * so it can be displayed to the user to help them add/edit filters.
	 */
	public readonly filterFieldDetails = computed(() => {

		const fields = getDistinctFilterFields(this.filters());
		const fandfs = this._fieldsAndFilters();

		const details = fields
			.map(field => fandfs?.find(fandf => fandf.field.field == field))
			.filter(fandf => !!fandf)
			.map(fandf => fandf.field)
			.map(field => {
				const parts = field.field.split(': ');
				return {
					group: parts[0]!,
					label: parts[1]!,
					desc: `${field.desc}  Examples: ${field.sample_values.join(', ')}`,
				};
			});

		return details;
	});
}
